import Image from 'next/legacy/image'
import clsx from 'clsx'

import Text from '~/components/Text/Text'
import Button from '~/components/Button/Button'
import Link from '~/components/Link/Link'
import { PAGE_ROUTES, GITHUB_CLI_REPO_URL } from '~/constants'
import Icon from '~/components/Icon/Icon'
import { useRepoStargazers } from '~/lib/useStargazers'

import styles from './CLIHero.module.sass'

interface Props {
  httpieRepoStargazers: number | undefined
}

const shapeAltText = 'Open Source. Open minded.'

const StarButton: React.FC<Props> = ({ httpieRepoStargazers }) => {
  const stargazersCount = useRepoStargazers(httpieRepoStargazers)

  return (
    <Link href={GITHUB_CLI_REPO_URL} data-track='social:star-on-github:cli-repo' className={styles.starButtonWrapper}>
      <span className={styles.starButton}>
        <Icon name='github' className={styles.starButtonIcon} />
        <Text fontSize={15} as='span' className={styles.starButtonText}>
          Star
        </Text>
      </span>
      {stargazersCount && (
        <Text fontSize={15} as='span' className={styles.starButtonBubble}>
          {stargazersCount.toLocaleString()}
        </Text>
      )}
    </Link>
  )
}

const CLIHero: React.FC<Props> = ({ httpieRepoStargazers }) => {
  return (
    <div className={clsx('site-grid site-container', styles.section)}>
      <div
        className={clsx(
          'col-span-full row-start-2 md:row-start-1 md:col-span-4 lg:col-span-5 lg:col-start-1 xl:col-start-2',
          styles.textSection
        )}
      >
        <Text variant='headline' fontSize={[55, 64, 105]} className={styles.headline}>
          HTTPie for <br /> Terminal
        </Text>

        <Text variant='body' fontSize={[18, 14, 18]} className={styles.description}>
          A simple yet powerful command-line HTTP and API testing client for the API era.
        </Text>

        <div className={styles.buttonsWrapper}>
          <div className={styles.buttonsColumn}>
            <Button
              variant='primary'
              className={styles.button}
              onClick={() => document.getElementById('installation')?.scrollIntoView({ behavior: 'smooth' })}
              data-track='cta:install-cli:from-cli-hero'
            >
              Install
            </Button>
            <StarButton httpieRepoStargazers={httpieRepoStargazers} />
          </div>

          <div className={styles.buttonsColumn}>
            <Button link={PAGE_ROUTES.DOCS_CLI} className={styles.button} data-track='cta:read-docs:from-cli-hero'>
              Read docs
            </Button>
            <Text fontSize={15} className={styles.appSectionLink}>
              <Link href={PAGE_ROUTES.RUN} className={styles.link} underlined data-track='cta:try-online:from-cli-hero'>
                Try online
              </Link>
            </Text>
          </div>
        </div>
      </div>

      <div className='flex justify-center col-span-2 row-start-1 md:col-span-4 lg:hidden'>
        <div className={styles.shape}>
          <Image
            src='/Images/cli-hero-mobile.png'
            alt={shapeAltText}
            layout='responsive'
            width={336}
            height={345}
            priority
          />
        </div>
      </div>

      <div className='hidden lg:flex lg:justify-center lg:col-start-7 lg:col-end-13'>
        <div className={styles.shape}>
          <Image src='/Images/cli-hero.png' alt={shapeAltText} layout='responsive' width={336} height={345} priority />
        </div>
      </div>
    </div>
  )
}

export default CLIHero
